import './IssueFeedback.css';
import React, { useEffect, useRef, useState } from 'react';
import { getAppBackendUrl } from '../../config';

const CONSENT_YES = 1;
const CONSENT_NO = 0;

function IssueFeedback({
  onRequestComplete,
  productType,
  conversationId = '',
  authorization = '',
  defaultEmail = '',
  defaultPhone = '',
}) {
  const API_URL = getAppBackendUrl();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [issueDescription, setIssueDescription] = useState('');
  const [followUpConsent, setFollowUpConsent] = useState(null); // null = unanswered, CONSENT_YES, CONSENT_NO
  const [followUpContact, setFollowUpContact] = useState('');
  const [profile, setProfile] = useState({ email: '', phone: '' });

  const issueRef = useRef(null);
  const contactRef = useRef(null);

  const authHeader = authorization || new URLSearchParams(window.location.search).get('authorization') || '';

  // On mount: parallel fetch user profile + last feedback to pre-fill follow-up answers.
  // issueDescription is intentionally always cleared per requirement.
  useEffect(() => {
    if (!authHeader) {
      return;
    }
    const headers = { Authorization: authHeader };
    const profilePromise = fetch(`${API_URL}/user/getUserById`, { headers })
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);
    const lastPromise = fetch(`${API_URL}/issue-feedback/last`, { headers })
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);
    Promise.all([profilePromise, lastPromise]).then(([prof, last]) => {
      if (prof && prof.code === 200 && prof.data) {
        setProfile({
          email: prof.data.email || '',
          phone: prof.data.phone || '',
        });
      }
      if (last && last.code === 200 && last.data) {
        if (last.data.followUpConsent === CONSENT_YES || last.data.followUpConsent === CONSENT_NO) {
          setFollowUpConsent(last.data.followUpConsent);
        }
        if (last.data.followUpContact) {
          setFollowUpContact(last.data.followUpContact);
        }
      }
    });
  }, [API_URL, authHeader]);

  // When user toggles to Yes and contact box is empty, prefill from profile → URL fallback.
  const handleSelectYes = () => {
    setFollowUpConsent(CONSENT_YES);
    if (!followUpContact) {
      const fallback = profile.email || profile.phone || defaultEmail || defaultPhone || '';
      if (fallback) setFollowUpContact(fallback);
    }
  };

  const handleSelectNo = () => {
    setFollowUpConsent(CONSENT_NO);
  };

  // contact 始终可选（即使选了 Yes 也允许空）
  const isFormActive =
    issueDescription.trim().length > 0 &&
    followUpConsent !== null;

  function callbackIssueFeedback(status, json) {
    console.log('[IssueFeedback] HTTP status:', status, 'responseText:', json);
    try {
      const jsonObject = window.JSON.parse(json);
      if (jsonObject.code === 200) {
        onRequestComplete();
      } else {
        setSubmitError("Your request wasn't sent. Please try again.");
        console.error('[IssueFeedback] error response:', jsonObject);
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('[IssueFeedback] JSON parse error:', error, 'raw response:', json);
      setSubmitError("Your request wasn't sent. Please try again.");
      setIsSubmitting(false);
    }
  }

  const submitIssueFeedback = (callback) => {
    const httpRequest = new XMLHttpRequest();
    httpRequest.open('POST', `${API_URL}/issue-feedback/submit/v2`, true);
    httpRequest.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
    if (authHeader) {
      httpRequest.setRequestHeader('Authorization', authHeader);
    }
    const data = JSON.stringify({
      productType: productType ? String(productType) : '',
      issueDescription,
      followUpConsent: followUpConsent === CONSENT_YES ? 1 : 0,
      followUpContact: followUpConsent === CONSENT_YES ? followUpContact.trim() : null,
      conversationId: conversationId || null,
    });
    httpRequest.send(data);
    httpRequest.onreadystatechange = function () {
      if (httpRequest.readyState === 4) {
        callback(httpRequest.status, httpRequest.responseText);
      }
    };
  };

  const handleSubmit = () => {
    if (issueDescription.trim() === '') {
      setSubmitError('Please describe your issue.');
      issueRef.current && issueRef.current.focus();
      return;
    }
    if (followUpConsent === null) {
      setSubmitError('Please tell us whether to follow up.');
      return;
    }
    setSubmitError('');
    setIsSubmitting(true);
    submitIssueFeedback(callbackIssueFeedback);
  };

  return (
    <div className="si-container">
      <div className="si-content">
        <div className="si-title">Contact Support</div>
        <div className="si-subtitle">Drop us your phone and email, we'll be in touch within 24 business hours.</div>

        <div className="si-label">Describe Your Issue</div>
        <div className="si-textarea-wrapper">
          <textarea
            ref={issueRef}
            className="si-textarea"
            placeholder="Please describe the problem you are experiencing"
            maxLength={999}
            value={issueDescription}
            onChange={(e) => setIssueDescription(e.target.value)}
          />
          <div className="si-char-count">{issueDescription.length}/999</div>
        </div>

        <div className="si-label">Would you like us to follow up with you?</div>

        <div
          className={`si-radio-row ${followUpConsent === CONSENT_YES ? 'si-radio-row--active' : ''}`}
          onClick={handleSelectYes}
        >
          <span className={`si-radio-dot ${followUpConsent === CONSENT_YES ? 'si-radio-dot--checked' : ''}`} />
          <span className="si-radio-text">Yes, I'm happy to be contacted</span>
        </div>

        {followUpConsent === CONSENT_YES && (
          <div className="si-contact-block">
            <div className="si-contact-hint">Please leave your phone number or email address:</div>
            <input
              ref={contactRef}
              className="si-contact-input"
              placeholder=""
              readOnly={isSubmitting}
              value={followUpContact}
              onChange={(e) => setFollowUpContact(e.target.value)}
            />
          </div>
        )}

        <div
          className={`si-radio-row ${followUpConsent === CONSENT_NO ? 'si-radio-row--active' : ''}`}
          onClick={handleSelectNo}
        >
          <span className={`si-radio-dot ${followUpConsent === CONSENT_NO ? 'si-radio-dot--checked' : ''}`} />
          <span className="si-radio-text">No, I prefer not to be contacted</span>
        </div>

        <div className="si-submit-wrapper">
          {submitError && <div className="si-submit-tooltip">{submitError}</div>}
          <button
            className={`si-submit-btn ${isFormActive ? 'si-submit-btn--active' : ''}`}
            disabled={!isFormActive || isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting ? 'Submitting...' : submitError ? 'Try Again' : 'Submit'}
          </button>
        </div>

        <div style={{ height: 40 }} />
      </div>
    </div>
  );
}

export default IssueFeedback;
